import { VehicleState } from '@shared/vehicleSlice';
import { Journey as RealTimeJourney } from '../models/real-time/journey';
import { Line } from '@shared/models/line';
import { DeepReadonly } from 'ts-essentials';
import { JourneyServiceType } from '../models/protobuf/Journey';
import { Destination } from '../models/destination';
import { Vehicle } from '../models/vehicle';
import createJourney from './createJourney';
import isVehicleOffRoute from './isVehicleOffRoute';

const FALLBACK_BACKGROUND_COLOR = '#006c93';
const FALLBACK_TEXT_COLOR = '#ffffff';

export default function createVehicle(
  vehicleState: DeepReadonly<VehicleState>,
  realTimeJourney: DeepReadonly<RealTimeJourney> | undefined,
  hasRealTimeError: boolean,
  hasExpiredVehicleState: boolean,
): Vehicle | undefined {
  if (hasExpiredVehicleState) {
    return undefined;
  }

  const isRealTimeJourneyMatchingVehicleJourney =
    realTimeJourney?.serviceJourneyGid === vehicleState.journeyGid;
  const realTimeJourneyToUse = isRealTimeJourneyMatchingVehicleJourney
    ? realTimeJourney
    : undefined;

  const isDeadrun =
    vehicleState.journeyServiceType === JourneyServiceType.JST_DEADRUN;
  return {
    line: isDeadrun
      ? undefined
      : createLine(vehicleState, realTimeJourneyToUse),
    destination: createDestination(
      vehicleState.destination,
      vehicleState.via,
      vehicleState.destination2,
    ),
    journey: isVehicleOffRoute(vehicleState)
      ? 'off-route'
      : createJourney(vehicleState, realTimeJourneyToUse, hasRealTimeError),
    isStopPressed: vehicleState.isStopPressed,
  };
}

function createLine(
  vehicleState: DeepReadonly<VehicleState>,
  realTimeJourney: DeepReadonly<RealTimeJourney> | undefined,
): Line | undefined {
  if (vehicleState.lineName === undefined) {
    return undefined;
  }

  return {
    shortName: vehicleState.lineName,
    backgroundColor:
      realTimeJourney?.lineBackgroundColor ?? FALLBACK_BACKGROUND_COLOR,
    textColor: realTimeJourney?.lineTextColor ?? FALLBACK_TEXT_COLOR,
  };
}

function getSecondaryDestination(
  via: string | undefined,
  destination2: string | undefined,
): string | undefined {
  if (via) {
    return via;
  }

  if (destination2) {
    return destination2;
  }

  return undefined;
}

function createDestination(
  destination: string | undefined,
  via: string | undefined,
  destination2: string | undefined,
): Destination | undefined {
  return destination
    ? {
        primary: destination,
        secondary: getSecondaryDestination(via, destination2),
      }
    : undefined;
}
